import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Title,
  Text,
  Container,
  Paper,
  ThemeIcon,
  Group,
  Stack,
  TextInput,
  Button,
  Tabs,
  Table,
  ActionIcon,
  Modal,
  NumberInput,
  Loader,
  Center,
  Badge,
  ScrollArea,
  Card,
  Divider,
} from '@mantine/core';
import {
  IconSettings,
  IconPlus,
  IconEdit,
  IconTrash,
  IconBox,
  IconBuilding,
  IconBuildingSkyscraper,
  IconCurrencyLira,
  IconArrowLeft,
  IconCheck,
  IconHash,
  IconBuildingBank,
  IconAlertTriangle,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import api from '../../api/axiosConfig';

const heroGradient =
  'linear-gradient(135deg, rgba(15,23,42,0.98) 0%, rgba(30,41,59,0.96) 35%, rgba(37,99,235,0.88) 100%)';

function formatMoney(value) {
  const num = Number(value || 0);
  return num.toLocaleString('tr-TR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function getErrorMessage(err, fallback = 'İşlem başarısız.') {
  const detail = err?.response?.data?.detail;

  if (Array.isArray(detail) && detail.length > 0) {
    return detail[0]?.msg || fallback;
  }

  if (typeof detail === 'string' && detail.trim()) {
    return detail;
  }

  if (err?.response?.data?.message) {
    return err.response.data.message;
  }

  return fallback;
}

export default function ConcreteSettings() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [concretes, setConcretes] = useState([]);
  const [sites, setSites] = useState([]);
  const [companies, setCompanies] = useState([]);

  const [concreteModalOpen, setConcreteModalOpen] = useState(false);
  const [siteModalOpen, setSiteModalOpen] = useState(false);
  const [companyModalOpen, setCompanyModalOpen] = useState(false);

  const [editingConcrete, setEditingConcrete] = useState(null);
  const [concreteForm, setConcreteForm] = useState({ name: '', price_per_m3: 0 });

  const [siteName, setSiteName] = useState('');

  const [editingCompany, setEditingCompany] = useState(null);
  const [companyForm, setCompanyForm] = useState({
    name: '',
    tax_no: null,
    tax_office: '',
  });

  const [feedbackModal, setFeedbackModal] = useState({
    opened: false,
    title: '',
    message: '',
    color: 'red',
  });

  const [deleteModal, setDeleteModal] = useState({
    opened: false,
    endpoint: '',
    id: null,
    label: '',
  });

  const openFeedbackModal = (title, message, color = 'red') => {
    setFeedbackModal({
      opened: true,
      title,
      message,
      color,
    });
  };

  const closeFeedbackModal = () => {
    setFeedbackModal({
      opened: false,
      title: '',
      message: '',
      color: 'red',
    });
  };

  const openDeleteModal = (endpoint, id, label) => {
    setDeleteModal({
      opened: true,
      endpoint,
      id,
      label,
    });
  };

  const closeDeleteModal = () => {
    setDeleteModal({
      opened: false,
      endpoint: '',
      id: null,
      label: '',
    });
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [cRes, sRes, compRes] = await Promise.all([
        api.get('/records/settings/concretes'),
        api.get('/records/settings/sites'),
        api.get('/records/settings/companies'),
      ]);

      setConcretes(cRes.data || []);
      setSites(sRes.data || []);
      setCompanies(compRes.data || []);
    } catch (err) {
      openFeedbackModal('Hata', 'Ayarlar yüklenemedi. Lütfen tekrar deneyin.', 'red');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleOpenConcreteModal = (concrete = null) => {
    if (concrete) {
      setEditingConcrete(concrete);
      setConcreteForm({
        name: concrete.name ?? '',
        price_per_m3: concrete.price_per_m3 ?? 0,
      });
    } else {
      setEditingConcrete(null);
      setConcreteForm({ name: '', price_per_m3: 0 });
    }
    setConcreteModalOpen(true);
  };

  const handleSaveConcrete = async () => {
    if (!concreteForm.name.trim()) {
      openFeedbackModal('Eksik Bilgi', 'Beton cinsi adı boş bırakılamaz.', 'orange');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        name: concreteForm.name.trim(),
        price_per_m3: Number(concreteForm.price_per_m3 || 0),
      };

      if (editingConcrete) {
        await api.put(`/records/settings/concretes/${editingConcrete.id}`, payload);
        notifications.show({
          title: 'Başarılı',
          message: 'Beton türü güncellendi.',
          color: 'teal',
          icon: <IconCheck size={16} />,
        });
      } else {
        await api.post('/records/settings/concretes', payload);
        notifications.show({
          title: 'Başarılı',
          message: 'Yeni beton türü eklendi.',
          color: 'teal',
          icon: <IconCheck size={16} />,
        });
      }

      setConcreteModalOpen(false);
      await fetchData();
    } catch (err) {
      openFeedbackModal('Hata', getErrorMessage(err, 'Beton kaydedilemedi.'), 'red');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveSite = async () => {
    if (!siteName.trim()) {
      openFeedbackModal('Eksik Bilgi', 'Şantiye adı boş bırakılamaz.', 'orange');
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/records/settings/sites', { name: siteName.trim() });
      notifications.show({
        title: 'Başarılı',
        message: 'Şantiye başarıyla eklendi.',
        color: 'teal',
        icon: <IconCheck size={16} />,
      });
      setSiteModalOpen(false);
      setSiteName('');
      await fetchData();
    } catch (err) {
      openFeedbackModal('Hata', getErrorMessage(err, 'Şantiye eklenemedi.'), 'red');
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenCompanyModal = (company = null) => {
    if (company) {
      setEditingCompany(company);
      setCompanyForm({
        name: company.name ?? '',
        tax_no: company.tax_no ?? null,
        tax_office: company.tax_office ?? '',
      });
    } else {
      setEditingCompany(null);
      setCompanyForm({
        name: '',
        tax_no: null,
        tax_office: '',
      });
    }
    setCompanyModalOpen(true);
  };

  const handleSaveCompany = async () => {
    const taxNoValue = companyForm.tax_no;

    if (!companyForm.name.trim() || taxNoValue === null || taxNoValue === undefined) {
      openFeedbackModal(
        'Eksik Bilgi',
        'Firma ünvanı ve vergi numarası zorunludur.',
        'orange'
      );
      return;
    }

    const taxNoString = String(taxNoValue).trim();
    if (!/^\d{10}$/.test(taxNoString)) {
      openFeedbackModal(
        'Geçersiz Vergi No',
        'Vergi numarası 10 haneli olmalıdır.',
        'orange'
      );
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        name: companyForm.name.trim(),
        tax_no: Number(taxNoString),
        tax_office: companyForm.tax_office.trim(),
      };

      if (editingCompany) {
        await api.put(`/records/settings/companies/${editingCompany.id}`, payload);
        notifications.show({
          title: 'Başarılı',
          message: 'Firma bilgileri güncellendi.',
          color: 'teal',
          icon: <IconCheck size={16} />,
        });
      } else {
        await api.post('/records/settings/companies', payload);
        notifications.show({
          title: 'Başarılı',
          message: 'Firma başarıyla eklendi.',
          color: 'teal',
          icon: <IconCheck size={16} />,
        });
      }

      setCompanyModalOpen(false);
      await fetchData();
    } catch (err) {
      openFeedbackModal('Hata', getErrorMessage(err, 'Firma kaydedilemedi.'), 'red');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModal.endpoint || deleteModal.id === null || deleteModal.id === undefined) {
      closeDeleteModal();
      return;
    }

    setSubmitting(true);
    try {
      await api.delete(`/records/settings/${deleteModal.endpoint}/${deleteModal.id}`);
      notifications.show({
        title: 'Başarılı',
        message: 'Kayıt silindi.',
        color: 'teal',
        icon: <IconCheck size={16} />,
      });
      closeDeleteModal();
      await fetchData();
    } catch (err) {
      openFeedbackModal('Hata', getErrorMessage(err, 'Silme işlemi başarısız.'), 'red');
    } finally {
      setSubmitting(false);
    }
  };

  const renderEmptyState = (title, message) => (
    <Card withBorder radius="xl" p="xl">
      <Stack align="center" gap="xs" py="lg">
        <ThemeIcon size={52} radius="xl" variant="light" color="blue">
          <IconBox size={26} />
        </ThemeIcon>
        <Text fw={700} size="lg">
          {title}
        </Text>
        <Text c="dimmed" ta="center" size="sm">
          {message}
        </Text>
      </Stack>
    </Card>
  );

  if (loading) {
    return (
      <Center h={400}>
        <Loader size="xl" />
      </Center>
    );
  }

  return (
    <Container fluid p={{ base: 'sm', md: 'lg' }}>
      <Stack gap="xl">
        <Paper
          radius="xl"
          p={{ base: 'lg', md: 'xl' }}
          style={{
            background: heroGradient,
            position: 'relative',
            overflow: 'hidden',
            border: '1px solid rgba(255,255,255,0.08)',
            boxShadow: '0 20px 60px rgba(15, 23, 42, 0.18)',
          }}
        >
          <Stack gap="md" style={{ position: 'relative', zIndex: 2 }}>
            <Group justify="space-between" align="flex-start" wrap="wrap">
              <Button
                variant="white"
                radius="xl"
                size="sm"
                leftSection={<IconArrowLeft size={16} />}
                onClick={() => navigate('/beton-takip')}
                style={{
                  background: 'rgba(255,255,255,0.14)',
                  color: 'white',
                  border: 'none',
                }}
              >
                Beton Takip Menüsüne Dön
              </Button>

              <Group gap="xs">
                <Badge variant="light" color="cyan" radius="xl">
                  {concretes.length} Beton
                </Badge>
                <Badge variant="light" color="teal" radius="xl">
                  {sites.length} Şantiye
                </Badge>
                <Badge variant="light" color="pink" radius="xl">
                  {companies.length} Firma
                </Badge>
              </Group>
            </Group>

            <Group gap="sm" align="center">
              <ThemeIcon size={42} radius="xl" variant="white" color="blue">
                <IconSettings size={22} />
              </ThemeIcon>
              <Stack gap={2}>
                <Text size="sm" fw={600} c="rgba(255,255,255,0.8)">
                  Araç Filo & Şantiye Yönetimi
                </Text>
                <Title order={1} fw={900} c="white">
                  Beton Takip Ayarları
                </Title>
              </Stack>
            </Group>

            <Text c="rgba(255,255,255,0.78)" size="sm" maw={800}>
              Beton türleri, iç teslimat şantiyeleri ve dış satış firmalarını tek ekrandan yönet.
              Temiz, hızlı ve düzenli bir operasyon ekranı.
            </Text>
          </Stack>
        </Paper>

        <Paper radius="xl" p={{ base: 'md', md: 'xl' }} withBorder shadow="sm">
          <Tabs defaultValue="concrete" variant="pills" radius="xl" color="blue">
            <Tabs.List grow mb="lg">
              <Tabs.Tab value="concrete" leftSection={<IconBox size={16} />}>
                Beton Türleri
              </Tabs.Tab>
              <Tabs.Tab value="sites" leftSection={<IconBuilding size={16} />}>
                Şantiyeler
              </Tabs.Tab>
              <Tabs.Tab value="companies" leftSection={<IconBuildingSkyscraper size={16} />}>
                Firmalar
              </Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="concrete">
              <Stack gap="md">
                <Group justify="space-between" align="center">
                  <div>
                    <Title order={4}>Kayıtlı Beton Cinsleri</Title>
                    <Text size="sm" c="dimmed">
                      Beton türleri ve metreküp fiyatları.
                    </Text>
                  </div>

                  <Button
                    leftSection={<IconPlus size={16} />}
                    radius="xl"
                    onClick={() => handleOpenConcreteModal()}
                  >
                    Yeni Beton Ekle
                  </Button>
                </Group>

                {concretes.length === 0 ? (
                  renderEmptyState(
                    'Henüz beton türü yok',
                    'İlk beton türünü ekleyerek listeyi başlat.'
                  )
                ) : (
                  <Card withBorder radius="xl" p={0}>
                    <ScrollArea>
                      <Table highlightOnHover striped withTableBorder verticalSpacing="md">
                        <Table.Thead>
                          <Table.Tr>
                            <Table.Th>Beton Cinsi</Table.Th>
                            <Table.Th>Birim Fiyatı (m³)</Table.Th>
                            <Table.Th style={{ width: 120, textAlign: 'right' }}>
                              İşlemler
                            </Table.Th>
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                          {concretes.map((c) => (
                            <Table.Tr key={c.id}>
                              <Table.Td fw={600}>{c.name}</Table.Td>
                              <Table.Td>{formatMoney(c.price_per_m3)} ₺</Table.Td>
                              <Table.Td style={{ textAlign: 'right' }}>
                                <Group gap="xs" justify="flex-end">
                                  <ActionIcon
                                    color="blue"
                                    variant="light"
                                    radius="lg"
                                    onClick={() => handleOpenConcreteModal(c)}
                                  >
                                    <IconEdit size={16} />
                                  </ActionIcon>
                                  <ActionIcon
                                    color="red"
                                    variant="light"
                                    radius="lg"
                                    onClick={() =>
                                      openDeleteModal('concretes', c.id, c.name || 'Bu beton türü')
                                    }
                                  >
                                    <IconTrash size={16} />
                                  </ActionIcon>
                                </Group>
                              </Table.Td>
                            </Table.Tr>
                          ))}
                        </Table.Tbody>
                      </Table>
                    </ScrollArea>
                  </Card>
                )}
              </Stack>
            </Tabs.Panel>

            <Tabs.Panel value="sites">
              <Stack gap="md">
                <Group justify="space-between" align="center">
                  <div>
                    <Title order={4}>İç Teslimat Şantiyeleri</Title>
                    <Text size="sm" c="dimmed">
                      Beton sevkiyatında kullanılan şantiye/alan listesi.
                    </Text>
                  </div>

                  <Button
                    leftSection={<IconPlus size={16} />}
                    radius="xl"
                    onClick={() => setSiteModalOpen(true)}
                  >
                    Yeni Şantiye Ekle
                  </Button>
                </Group>

                {sites.length === 0 ? (
                  renderEmptyState(
                    'Henüz şantiye yok',
                    'İlk şantiyeyi ekleyerek listeyi başlat.'
                  )
                ) : (
                  <Card withBorder radius="xl" p={0}>
                    <ScrollArea>
                      <Table highlightOnHover striped withTableBorder verticalSpacing="md">
                        <Table.Thead>
                          <Table.Tr>
                            <Table.Th>Şantiye Adı</Table.Th>
                            <Table.Th style={{ width: 120, textAlign: 'right' }}>
                              İşlemler
                            </Table.Th>
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                          {sites.map((s) => (
                            <Table.Tr key={s.id}>
                              <Table.Td fw={500}>{s.name}</Table.Td>
                              <Table.Td style={{ textAlign: 'right' }}>
                                <Group gap="xs" justify="flex-end">
                                  <ActionIcon
                                    color="red"
                                    variant="light"
                                    radius="lg"
                                    onClick={() =>
                                      openDeleteModal('sites', s.id, s.name || 'Bu şantiye')
                                    }
                                  >
                                    <IconTrash size={16} />
                                  </ActionIcon>
                                </Group>
                              </Table.Td>
                            </Table.Tr>
                          ))}
                        </Table.Tbody>
                      </Table>
                    </ScrollArea>
                  </Card>
                )}
              </Stack>
            </Tabs.Panel>

            <Tabs.Panel value="companies">
              <Stack gap="md">
                <Group justify="space-between" align="center">
                  <div>
                    <Title order={4}>Dış Satış Firmaları</Title>
                    <Text size="sm" c="dimmed">
                      Fatura ve dış satış için kullanılan firma kayıtları.
                    </Text>
                  </div>

                  <Button
                    color="pink"
                    leftSection={<IconPlus size={16} />}
                    radius="xl"
                    onClick={() => handleOpenCompanyModal()}
                  >
                    Yeni Firma Ekle
                  </Button>
                </Group>

                {companies.length === 0 ? (
                  renderEmptyState(
                    'Henüz firma yok',
                    'İlk firmayı ekleyerek listeyi başlat.'
                  )
                ) : (
                  <Card withBorder radius="xl" p={0}>
                    <ScrollArea>
                      <Table highlightOnHover striped withTableBorder verticalSpacing="md">
                        <Table.Thead>
                          <Table.Tr>
                            <Table.Th>Firma Ünvanı</Table.Th>
                            <Table.Th>Vergi No</Table.Th>
                            <Table.Th>Vergi Dairesi</Table.Th>
                            <Table.Th style={{ width: 120, textAlign: 'right' }}>
                              İşlemler
                            </Table.Th>
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                          {companies.map((c) => (
                            <Table.Tr key={c.id}>
                              <Table.Td fw={500}>{c.name}</Table.Td>
                              <Table.Td>{c.tax_no ?? '-'}</Table.Td>
                              <Table.Td>{c.tax_office || '-'}</Table.Td>
                              <Table.Td style={{ textAlign: 'right' }}>
                                <Group gap="xs" justify="flex-end">
                                  <ActionIcon
                                    color="blue"
                                    variant="light"
                                    radius="lg"
                                    onClick={() => handleOpenCompanyModal(c)}
                                  >
                                    <IconEdit size={16} />
                                  </ActionIcon>
                                  <ActionIcon
                                    color="red"
                                    variant="light"
                                    radius="lg"
                                    onClick={() =>
                                      openDeleteModal('companies', c.id, c.name || 'Bu firma')
                                    }
                                  >
                                    <IconTrash size={16} />
                                  </ActionIcon>
                                </Group>
                              </Table.Td>
                            </Table.Tr>
                          ))}
                        </Table.Tbody>
                      </Table>
                    </ScrollArea>
                  </Card>
                )}
              </Stack>
            </Tabs.Panel>
          </Tabs>
        </Paper>
      </Stack>

      <Modal
        opened={concreteModalOpen}
        onClose={() => setConcreteModalOpen(false)}
        title={editingConcrete ? 'Beton Cinsi Düzenle' : 'Yeni Beton Ekle'}
        radius="xl"
        centered
        size="md"
      >
        <Stack gap="md">
          <TextInput
            label="Beton Cinsi"
            value={concreteForm.name}
            onChange={(e) => setConcreteForm({ ...concreteForm, name: e.target.value })}
            radius="md"
            required
            autoFocus
          />
          <NumberInput
            label="Birim Fiyatı (m³)"
            value={concreteForm.price_per_m3}
            onChange={(val) =>
              setConcreteForm({ ...concreteForm, price_per_m3: val ?? 0 })
            }
            leftSection={<IconCurrencyLira size={16} />}
            radius="md"
            min={0}
            decimalScale={2}
            fixedDecimalScale
            thousandSeparator="."
            decimalSeparator=","
            required
          />

          <Divider />

          <Button
            radius="xl"
            onClick={handleSaveConcrete}
            loading={submitting}
            fullWidth
          >
            Kaydet
          </Button>
        </Stack>
      </Modal>

      <Modal
        opened={siteModalOpen}
        onClose={() => setSiteModalOpen(false)}
        title="Yeni Şantiye Ekle"
        radius="xl"
        centered
        size="md"
      >
        <Stack gap="md">
          <TextInput
            label="Şantiye / Alan Adı"
            value={siteName}
            onChange={(e) => setSiteName(e.target.value)}
            radius="md"
            required
            autoFocus
          />

          <Divider />

          <Button
            radius="xl"
            onClick={handleSaveSite}
            loading={submitting}
            fullWidth
          >
            Kaydet
          </Button>
        </Stack>
      </Modal>

      <Modal
        opened={companyModalOpen}
        onClose={() => setCompanyModalOpen(false)}
        title={editingCompany ? 'Firma Bilgilerini Düzenle' : 'Yeni Firma Ekle'}
        radius="xl"
        centered
        size="md"
      >
        <Stack gap="md">
          <TextInput
            label="Firma Ünvanı"
            placeholder="Örn: ABC İnşaat A.Ş."
            required
            value={companyForm.name}
            onChange={(e) => setCompanyForm({ ...companyForm, name: e.target.value })}
            radius="md"
            autoFocus
          />

          <NumberInput
            label="Vergi Numarası"
            placeholder="10 haneli numara"
            required
            hideControls
            leftSection={<IconHash size={16} />}
            value={companyForm.tax_no}
            onChange={(val) => setCompanyForm({ ...companyForm, tax_no: val })}
            radius="md"
            allowNegative={false}
            thousandSeparator=""
          />

          <TextInput
            label="Vergi Dairesi"
            placeholder="Örn: Boğaziçi V.D."
            leftSection={<IconBuildingBank size={16} />}
            value={companyForm.tax_office}
            onChange={(e) => setCompanyForm({ ...companyForm, tax_office: e.target.value })}
            radius="md"
          />

          <Divider />

          <Button
            color="pink"
            radius="xl"
            onClick={handleSaveCompany}
            loading={submitting}
            fullWidth
          >
            Kaydet
          </Button>
        </Stack>
      </Modal>

      <Modal
        opened={feedbackModal.opened}
        onClose={closeFeedbackModal}
        title={feedbackModal.title}
        radius="xl"
        centered
        size="md"
      >
        <Stack gap="md">
          <Group gap="sm" align="flex-start">
            <ThemeIcon
              size={38}
              radius="xl"
              variant="light"
              color={feedbackModal.color === 'orange' ? 'yellow' : feedbackModal.color}
            >
              {feedbackModal.color === 'red' || feedbackModal.color === 'orange' ? (
                <IconAlertTriangle size={20} />
              ) : (
                <IconCheck size={20} />
              )}
            </ThemeIcon>
            <Text size="sm" lh={1.6}>
              {feedbackModal.message}
            </Text>
          </Group>

          <Button radius="xl" onClick={closeFeedbackModal} fullWidth>
            Tamam
          </Button>
        </Stack>
      </Modal>

      <Modal
        opened={deleteModal.opened}
        onClose={closeDeleteModal}
        title="Silme Onayı"
        radius="xl"
        centered
        size="md"
      >
        <Stack gap="md">
          <Group gap="sm" align="flex-start">
            <ThemeIcon size={38} radius="xl" variant="light" color="red">
              <IconTrash size={20} />
            </ThemeIcon>
            <Text size="sm" lh={1.6}>
              <Text span fw={700}>
                {deleteModal.label || 'Bu kayıt'}
              </Text>{' '}
              silinecek. Bu işlem geri alınamaz.
            </Text>
          </Group>

          <Group grow>
            <Button variant="default" radius="xl" onClick={closeDeleteModal} disabled={submitting}>
              Vazgeç
            </Button>
            <Button color="red" radius="xl" onClick={handleDeleteConfirm} loading={submitting}>
              Evet, Sil
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}